import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import apiClient from "../../api/client";
import IncrementField from "../../components/common/IncrementField";
import { effPPM } from "../../utils/rating";

interface RateRow {
  id: string;
  destination_prefix: string;
  rate: number;
  connect_fee?: number;
  billing_increment?: number;
  carrier_rate: number | null;
  margin_percent_applied: number | null;
  updated_at: string;
}

interface ImportResult {
  imported_count: number;
  error_count: number;
  errors: string[];
  total_processed: number;
  margin_percent_applied: number;
  margin_floor_applied: number;
}

/**
 * Reseller rate management (Wave 5.9): view/search own rates, edit a rate,
 * import a cost CSV (`prefix,carrier_rate`) — margins default to the
 * account's configured margin.
 */
export default function ResellerRatesPage() {
  const [rates, setRates] = useState<RateRow[]>([]);
  const [total, setTotal] = useState(0);
  const [prefixFilter, setPrefixFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Edit dialog state
  const [editing, setEditing] = useState<RateRow | null>(null);
  const [editRate, setEditRate] = useState("");
  const [editFee, setEditFee] = useState("0");
  const [editInc, setEditInc] = useState("1");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const fetchRates = useCallback(
    async (filter = prefixFilter) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ limit: "500" });
        if (filter) params.set("prefix_filter", filter);
        const response = await apiClient.get(`/api/v1/resellers/me/rates?${params}`);
        setRates(response.data.rates || []);
        setTotal(response.data.total || 0);
      } catch (err: any) {
        setError(err.response?.data?.detail || "Failed to fetch rates");
      } finally {
        setLoading(false);
      }
    },
    [prefixFilter]
  );

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (row: RateRow) => {
    setEditing(row);
    setEditRate(String(row.rate));
    setEditFee(String(row.connect_fee ?? 0));
    setEditInc(String(row.billing_increment ?? 1));
    setEditError("");
  };

  const saveEdit = async () => {
    if (!editing) return;
    const value = parseFloat(editRate);
    const inc = parseInt(editInc, 10) || 1;
    if (isNaN(value) || value <= 0) {
      setEditError("Rate must be a positive number");
      return;
    }
    if (!(inc >= 1 && inc <= 3600)) {
      setEditError("Billing increment must be 1–3600 seconds");
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await apiClient.put(
        `/api/v1/resellers/me/rates/${encodeURIComponent(editing.destination_prefix)}`,
        { rate: value, connect_fee: parseFloat(editFee) || 0, billing_increment: inc }
      );
      setEditing(null);
      await fetchRates();
    } catch (err: any) {
      setEditError(err.response?.data?.detail || "Failed to update rate");
    } finally {
      setSaving(false);
    }
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    setError("");
    try {
      const form = new FormData();
      form.append("file", importFile);
      const response = await apiClient.post("/api/v1/resellers/me/import-rates", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(response.data);
      await fetchRates();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Import failed");
      setImportOpen(false);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Rate Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The rates your downstream customers pay. Import a cost CSV — your
            configured margin produces customer rates automatically.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => { setImportOpen(true); setImportResult(null); }}>
          Import rate deck
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Filter by prefix (e.g. 44)"
          value={prefixFilter}
          onChange={(e) => setPrefixFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchRates()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
        <Button sx={{ ml: 1 }} onClick={() => fetchRates()} disabled={loading}>
          Search
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Destination prefix</TableCell>
              <TableCell align="right">Rate ($/min)</TableCell>
              <TableCell align="right">Carrier cost</TableCell>
              <TableCell align="right">Margin applied</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {row.destination_prefix}
                  </Typography>
                </TableCell>
                <TableCell align="right">{row.rate?.toFixed(4)}</TableCell>
                <TableCell align="right">
                  {row.carrier_rate != null ? row.carrier_rate.toFixed(4) : "—"}
                </TableCell>
                <TableCell align="right">
                  {row.margin_percent_applied != null ? `${row.margin_percent_applied}%` : "—"}
                </TableCell>
                <TableCell>
                  {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit rate">
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rates.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No rates found{prefixFilter ? ` for prefix "${prefixFilter}"` : ""}. Import a
                    rate deck to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        {total} rate{total === 1 ? "" : "s"} total{prefixFilter ? ` (filtered)` : ""}
      </Typography>

      {/* Edit dialog */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit rate — prefix <code>{editing?.destination_prefix}</code>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Customer rate ($/min)"
            type="number"
            value={editRate}
            onChange={(e) => setEditRate(e.target.value)}
            error={!!editError}
            helperText={editError || "Positive number; billed to your downstream accounts"}
            inputProps={{ step: "0.0001", min: 0.0001 }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Connect fee ($, optional)"
            type="number"
            value={editFee}
            onChange={(e) => setEditFee(e.target.value)}
            inputProps={{ step: "0.0001", min: 0 }}
          />
          <IncrementField value={editInc} onChange={setEditInc} />
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Eff. PPM @60s incl. connect fee:{" "}
            {effPPM(parseFloat(editRate) || 0, parseFloat(editFee) || 0, parseInt(editInc, 10) || 1).toFixed(6)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Carrier cost and margin are upstream-derived and cannot be edited here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving || !!editError}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import rate deck</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Upload a CSV with columns <code>prefix,carrier_rate</code> — optionally
            followed by <code>connect_fee</code> and <code>billing_increment</code>
            (seconds, 1–3600). Your account margin is applied automatically to
            produce customer rates.
          </Typography>
          <Box sx={{ fontFamily: "monospace", bgcolor: "action.hover", p: 1.5, borderRadius: 1, mb: 2, fontSize: 13 }}>
            prefix,carrier_rate,connect_fee,billing_increment
            <br />
            44,0.0150,0,1
            <br />
            1,0.0100,0.01,6
          </Box>
          <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
            {importFile ? importFile.name : "Choose CSV file"}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              ref={fileInput}
              onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
            />
          </Button>

          {importResult && (
            <Alert severity={importResult.error_count === 0 ? "success" : "warning"} sx={{ mt: 2 }}>
              Imported {importResult.imported_count}/{importResult.total_processed} rows
              (margin {importResult.margin_percent_applied}%).
              {importResult.error_count > 0 && ` ${importResult.error_count} errors: ${importResult.errors.slice(0, 3).join("; ")}`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={runImport}
            disabled={!importFile || importing}
            startIcon={<UploadIcon />}
          >
            {importing ? "Importing…" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
